import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CARD_SECTION_TITLE_CLASS,
  FORM_CONTROL_CLASS,
  FORM_LABEL_CLASS,
  Modal,
  TextButton,
} from '@safetyhub/ui';
import { MODAL_LAYER, SUB_MODAL_OVERLAY_BG } from '../../components/modalLayers';
// 토스트 문구는 `apiErrorMessage(err) ?? '<일반 문구>'`다 — `extractApiError(...).message`는 네트워크·알 수 없는
// 오류에서 axios 원문(「Network Error」 등)을 그대로 띄웠다(리뷰 1103 F7). 코드 분기만 `extractApiError`를 쓴다.
import { apiErrorMessage } from '../../lib/api';
import { useAuthStore } from '../../stores/auth';
import { useDeviceLayout } from '../../hooks/useDeviceLayout';
import { usePermissions } from '../../hooks/usePermissions';
import {
  organizationsApi,
  Organization,
  UpdateOrganizationDto,
  OrganizationCategory,
  OrganizationMember,
  LeaveOrganizationOptions,
  LeaveOrganizationResult,
  LeaveScenario,
  VerificationDocument,
  VerificationDocumentType,
  VerificationStatus,
  dataExportApi,
  DownloadMethod,
  ExportStatus,
  extractApiError,
  CATEGORY_OPTIONS,
  REJECTION_REASON_LABELS,
  isManualReviewVerificationStatus,
  verificationBand,
  classifyVerificationDocuments,
  hasRequiredVerificationDocs,
  requiredVerificationDocType,
  verificationUploadSurface,
} from '../../lib/organizations';
import { isOrganizationMember } from '../../lib/members';
import AddressSearch from '../../components/AddressSearch';
import { MemberSelect } from '../../components/MemberSelect';
import { DirtyHint } from '../../components/common/DirtyHint';
import { useToastStore } from '../../stores/toast';
import ChangeCategoryModal from '../../components/modals/ChangeCategoryModal';
import CompanyClaimSection from '../../components/settings/CompanyClaimSection';
import TenantMergeSection from '../../components/settings/TenantMergeSection';
import type { CompanyVerificationStatus } from '../../lib/companies';
import {
  companyTrustLevel,
  resolveAiMeasureTone,
  VERIFICATION_DOCUMENT_TYPE_LABELS,
} from '@safetyhub/shared';
import type { AiMeasureTone } from '@safetyhub/shared';
import { AI_MEASURE_TONE_PRESETS, toStoredAiMeasureTone } from '../../lib/aiMeasureTone';
import { useCompanyDuplicateModal } from '../../hooks/useCompanyDuplicateModal';
import { useDirtySnapshot } from '../../hooks/useDirtySnapshot';
import { useEntityChanged } from '../../hooks/useEntityChanged';

// 아이콘 컴포넌트
const ExclamationTriangleIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
    />
  </svg>
);

const XMarkIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const DownloadIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
    />
  </svg>
);

export default function OrganizationSettings() {
  const navigate = useNavigate();
  const { user, currentMembership, fetchUser } = useAuthStore();
  const companyId = currentMembership?.companyId;
  /**
   * 이 화면은 **두 축**을 쓴다(company-rbac P4 ① 규약 · P7-g 2026-09-25).
   *
   * - `canManageOrgSettings` = `can('org.settings.manage')` — 「이 행위를 해도 되나」. 어디가 이 축인지는
   *   **서버 게이트가 정한다**: 그 자리가 부르는 핸들러가 `@Permissions('org.settings.manage')`를 달고
   *   있으면 키다(`PATCH :organizationId` update · 로고 · 검증 서류 업로드·삭제).
   * - `isOwner` = 소유자 **성질**. 권한 key로 표현되지 않는 판정에만 남긴다 — 정본 클레임·조직 통합
   *   (서버 `companies.controller`가 「본인 조직 OWNER만」이라 권한 키가 아예 없다). 탈퇴 시나리오
   *   (소유권 이전·조직 삭제 선택지)는 이 값이 아니라 **멤버 목록 행의 `isOwner`** 로 센다 — 「내가
   *   유일한 소유자인가」는 서버 `leaveOrganization`과 같은 축이어야 한다.
   *
   * 소유자는 서버가 전체 키를 주므로(`resolvePermissions`) 키 항이 소유자를 **이미 포함**한다 —
   * `can(key) || isOwner`는 중복이자 규약 위반이다.
   */
  const { isOwner, can } = usePermissions();
  const canManageOrgSettings = can('org.settings.manage');
  const { showToast } = useToastStore();
  const deviceLayout = useDeviceLayout();
  const isMobile = deviceLayout === 'phone' || deviceLayout === 'fold';

  // 상태
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // 운영 설정 폼 상태
  const [name, setName] = useState('');
  const [category, setCategory] = useState<OrganizationCategory>('WORKPLACE');
  const [timezone, setTimezone] = useState('Asia/Seoul');
  const [allowNonAssignedSiteView, setAllowNonAssignedSiteView] = useState(false);

  // AI 작성 문체 — 저장값(null = 조직 유형에서 파생). 화면 선택은 resolveAiMeasureTone으로 파생해 그린다.
  const [aiMeasureTone, setAiMeasureTone] = useState<AiMeasureTone | null>(null);
  const [isSavingTone, setIsSavingTone] = useState(false);

  // 사업자 정보 폼 상태 (Company 기반)
  const [companyName, setCompanyName] = useState('');
  const [companyRegistrationNumber, setCompanyRegistrationNumber] = useState('');
  const [companyCeoName, setCompanyCeoName] = useState('');
  const [companyOpeningDate, setCompanyOpeningDate] = useState('');
  const [companyIndustry, setCompanyIndustry] = useState('');
  const [companyContact, setCompanyContact] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyZonecode, setCompanyZonecode] = useState('');
  const [companyAddressDetail, setCompanyAddressDetail] = useState('');
  const [isSavingCompany, setIsSavingCompany] = useState(false);

  // 회사 인증(클레임) 판단에 쓰는 Company 축 상태.
  // ⚠️ 아래 verificationStatus state와 다른 축이다 — 저쪽은 기관 자격 심사(agencyVerificationStatus),
  // 이쪽은 국세청 4필드 진위(company.verificationStatus)다.
  // organization state에서 파생하지 않고 따로 두는 이유: PATCH /organizations/:id 응답은
  // findById와 달리 Company 행을 그대로 내려줘 `organization.company`가 사라진다(저장 후 파생값이 비어버림).
  const [companyNtsStatus, setCompanyNtsStatus] = useState<CompanyVerificationStatus | undefined>(undefined);
  const [companyClaimedAt, setCompanyClaimedAt] = useState<string | null>(null);

  // FULLY Company 중복 차단 모달 (ORGANIZATION_DUPLICATE_COMPANY)
  const { handleError: handleCompanyDuplicateError, modalNode: companyDuplicateModal } =
    useCompanyDuplicateModal();

  // 탈퇴 관련 상태
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [leaveMode, setLeaveMode] = useState<'transfer' | 'delete' | null>(null);
  const [selectedNewOwner, setSelectedNewOwner] = useState<string>('');
  const [confirmOrgName, setConfirmOrgName] = useState('');
  const [leaveStep, setLeaveStep] = useState<'choice' | 'confirm'>('choice');

  // 데이터 다운로드 관련 상태
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadMethod, setDownloadMethod] = useState<DownloadMethod>('direct');
  const [exportId, setExportId] = useState<string | null>(null);
  const [exportStatus, setExportStatus] = useState<ExportStatus | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [downloadStep, setDownloadStep] = useState<'method' | 'processing' | 'ready'>('method');

  // 인증 관련 상태
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus | undefined>(undefined);
  const [verificationDocuments, setVerificationDocuments] = useState<VerificationDocument[]>([]);
  const [isUploadingDesignation, setIsUploadingDesignation] = useState(false);
  const [isDeletingDocId, setIsDeletingDocId] = useState<string | null>(null);
  const [isDownloadingDocId, setIsDownloadingDocId] = useState<string | null>(null);
  const businessRegFileInputRef = useRef<HTMLInputElement>(null);
  const designationFileInputRef = useRef<HTMLInputElement>(null);

  // 수동 심사 배치 업로드용 pending state
  const [pendingBusinessReg, setPendingBusinessReg] = useState<File | null>(null);
  const [pendingDesignation, setPendingDesignation] = useState<File | null>(null);
  const [isBatchUploading, setIsBatchUploading] = useState(false);

  // 카테고리 변경 모달 상태
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  // 운영 설정 폼 dirty 게이트 — 아래 사업자 정보의 isCompanyDirty와 같은 결(스냅샷 비교)이다.
  // name은 WORKSPACE에서만 편집 가능하지만 스냅샷에는 늘 담는다(편집 불가 조직에선 변하지 않는다).
  const orgFormDirty = useDirtySnapshot(() => ({
    name: name.trim(),
    timezone,
    allowNonAssignedSiteView,
  }));
  const { capture: captureOrgBaseline } = orgFormDirty;
  // AI 작성 문체 카드 dirty 게이트 — 저장 단위가 다른 카드(사업자 정보처럼 자기 저장 버튼)라 스냅샷도 따로 둔다.
  // 스냅샷은 **저장값**(null 포함)이다 — 파생값과 같은 카드를 다시 고르면 null로 돌아와 무변경이 된다.
  const toneDirty = useDirtySnapshot(() => ({ aiMeasureTone }));
  const { capture: captureToneBaseline } = toneDirty;
  // 로드 완료 세대 — silent 재조회(ENTITY_CHANGED)는 isLoading을 안 움직이므로 이 값으로 재캡처를 깨운다.
  const [loadGeneration, setLoadGeneration] = useState(0);
  // 기준점은 **로드 완료 시점**의 값 — 조직을 다시 불러오면(카테고리 변경·실시간 재조회 등) 세대가
  // 오르며 재캡처된다. 로드 전 캡처는 무해하다(스피너 동안 폼 자체가 렌더되지 않는다).
  useEffect(() => {
    if (!isLoading) {
      captureOrgBaseline();
      captureToneBaseline();
    }
  }, [isLoading, loadGeneration, captureOrgBaseline, captureToneBaseline]);

  // 권한 체크 — 이 화면의 편집 대상(조직 정보·본사 정보·로고·문체·조직 유형)이 전부 서버에서
  // `org.settings.manage`로 가드되므로 진입도 그 키다. 소유자 전용 구역은 아래에서 `isOwner`로 따로 가린다.
  useEffect(() => {
    if (!canManageOrgSettings) {
      navigate('/');
    }
  }, [canManageOrgSettings, navigate]);

  useEffect(() => {
    if (companyId) {
      loadOrganization();
    }
  }, [companyId]);

  // silent: 실시간 재조회(ENTITY_CHANGED) 경로 — 스피너로 폼을 갈아 끼우지 않고 값만 새로 채운다.
  const loadOrganization = async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!companyId) return;

    // soft delete된 조직인 경우 로드하지 않음
    if (currentMembership?.company.deletedAt) {
      navigate('/');
      return;
    }

    if (!silent) setIsLoading(true);
    try {
      const org = await organizationsApi.findById(companyId);
      setOrganization(org);
      setName(org.name ?? '');
      setCategory(org.category);
      setTimezone(org.timezone);
      setAllowNonAssignedSiteView(org.allowNonAssignedSiteView || false);
      setAiMeasureTone(org.aiMeasureTone ?? null);
      // 사업자 정보 초기화 (Company 기반)
      setCompanyName(org.company?.name || '');
      setCompanyRegistrationNumber(org.company?.registrationNumber || '');
      setCompanyCeoName(org.company?.ceoName || '');
      setCompanyOpeningDate(org.company?.openingDate || '');
      setCompanyIndustry(org.company?.industry || '');
      setCompanyContact(org.company?.contact || '');
      setCompanyEmail(org.company?.email || '');
      setCompanyAddress(org.company?.address || '');
      setCompanyZonecode(org.company?.zonecode || '');
      setCompanyAddressDetail(org.company?.addressDetail || '');
      // 회사 인증(클레임) 축 — 기관 자격 심사와 별개
      setCompanyNtsStatus(org.company?.verificationStatus);
      setCompanyClaimedAt(org.company?.claimedAt ?? null);
      // 인증 상태 초기화
      setVerificationStatus(org.verificationStatus);
      setVerificationDocuments(org.verificationDocuments || []);
    } catch (err) {
      const apiError = extractApiError(err);
      // 조직을 찾을 수 없는 경우 (404) 홈으로 이동 — silent 여부와 무관하다(조직이 사라졌다).
      if (apiError.code === 'ORGANIZATION_NOT_FOUND') {
        navigate('/');
        return;
      }
      // silent 재조회(실시간 이벤트)의 실패는 **알리지 않는다** — 사용자가 아무것도 하지 않았는데
      // 에러 토스트가 뜨면 자기 행동이 실패한 줄 안다. 실패해도 다음 이벤트·다음 수동 조회에서
      // 복구된다(`Groups.tsx`의 silent 경로와 같은 규약).
      if (!silent) showToast(apiErrorMessage(err) ?? '조직 정보를 불러오지 못했습니다.', 'error');
    } finally {
      setIsLoading(false);
      // silent 로드는 isLoading이 안 움직이므로 세대 카운터로 기준점 재캡처 effect를 깨운다.
      setLoadGeneration((g) => g + 1);
    }
  };

  // 서류 업로드 (DESIGNATION_CERT 전용 — BUSINESS_REGISTRATION은 UI에서 제거됨)
  const handleUploadVerificationDocument = async (file: File, type: VerificationDocumentType) => {
    if (!companyId) return;
    if (type === 'BUSINESS_REGISTRATION') return;
    setIsUploadingDesignation(true);
    try {
      const result = await organizationsApi.uploadVerificationDocument(companyId, file, type);
      setVerificationDocuments((prev) => [...prev, result.document]);
      setVerificationStatus(result.verificationStatus);
      showToast('지정서가 업로드되었습니다.', 'success');
    } catch (err) {
      const apiError = extractApiError(err);
      if (apiError.code === 'ORGANIZATION_VERIFICATION_NOT_ESCALATED') {
        showToast('자동 재검증이 진행 중입니다. 잠시 후 다시 시도해 주세요.', 'error');
      } else {
        showToast(apiErrorMessage(err) ?? '지정서를 업로드하지 못했습니다.', 'error');
      }
    } finally {
      setIsUploadingDesignation(false);
    }
  };

  // 수동 심사 배치 업로드
  const handleBatchUpload = async () => {
    if (!companyId || (!pendingBusinessReg && !pendingDesignation)) return;
    setIsBatchUploading(true);
    try {
      if (pendingBusinessReg) {
        const result = await organizationsApi.uploadVerificationDocument(
          companyId,
          pendingBusinessReg,
          'BUSINESS_REGISTRATION',
        );
        setVerificationDocuments((prev) => [...prev, result.document]);
        setVerificationStatus(result.verificationStatus);
        // 성공한 칸은 «그 자리에서» 비운다 — 종전엔 둘 다 끝난 뒤에 비워, 두 번째가 실패하면 첫 번째가
        // 대기 칸에 남아 재시도 때 **같은 서류가 두 번** 올라갔다(리뷰 1103 F4).
        setPendingBusinessReg(null);
      }
      if (pendingDesignation) {
        const result = await organizationsApi.uploadVerificationDocument(
          companyId,
          pendingDesignation,
          'DESIGNATION_CERT',
        );
        setVerificationDocuments((prev) => [...prev, result.document]);
        setVerificationStatus(result.verificationStatus);
        setPendingDesignation(null);
      }
      showToast('서류가 업로드되었습니다. 운영자 심사를 기다려 주세요.', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err) ?? '서류를 업로드하지 못했습니다.', 'error');
    } finally {
      setIsBatchUploading(false);
    }
  };

  /**
   * 검증 서류 열람 — **인증 전용 라우트**라 blob으로 받아 내려받는다(2026-10-19).
   *
   * ⚠️ `doc.url`을 `href`에 넣던 종전 방식으로 되돌리지 말 것. 그 값은 prod에서 **버킷 공개면
   * (r2.dev) URL**이었고(= 무인증·영구), 서버가 그 purpose를 `AUTH_ONLY`로 좁힌 뒤로는 응답이 공개
   * URL을 싣지 않는다. 서버 인증은 Bearer 헤더 전용이라 평범한 내비게이션이 **401**이므로 이 경로가
   * 유일한 열람 수단이다. 선례는 같은 화면의 `handleDownloadFile`(자료 내보내기 ZIP)이다.
   *
   * ## UX는 **「내려받기」로 확정**이다 (2026-10-19 사용자 결정)
   * 파일명 클릭이 종전의 「새 탭에서 열기」가 아니라 내려받기다. 선택지 셋을 올려 받은 결정이고
   * 기각 사유가 기록으로 남아야 한다:
   * - ✅ **내려받기** — 채택. 같은 화면의 `handleDownloadFile`(자료 내보내기 ZIP)과 같은 형태다.
   * - ❌ 새 탭 유지 — `await` 뒤의 `window.open`은 **사용자 동작으로 보이지 않아** 팝업 차단에
   *   걸릴 수 있다(그러면 「눌렀는데 아무 일도 없다」가 된다).
   * - ❌ 화면 안 미리보기 — 범위가 크다(뷰어·PDF 렌더).
   *
   * ### 👁 미리보기로 바꾸려면 **여기** — `link.click()` 대신 `window.open(objectUrl, '_blank')`
   * 그 한 줄이 전환점이고, 서버는 **이미 둘 다 지원한다**(라우트가 `isImage ? inline : attachment`로
   * `Content-Disposition`을 정하므로 blob의 `type`도 그대로 온다) ⇒ **서버를 고칠 필요가 없다.**
   * 바꿀 때 함께 볼 것: 위 팝업 차단 축(차단되면 폴백으로 내려받기) · 파일명이 사라지는 것
   * (blob URL은 이름이 없어 미리보기 탭 제목이 해시가 된다).
   */
  const handleDownloadVerificationDocument = async (doc: VerificationDocument) => {
    if (!companyId) return;
    setIsDownloadingDocId(doc.id);
    try {
      const blob = await organizationsApi.downloadVerificationDocument(companyId, doc.id);
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = doc.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // 즉시 revoke하면 일부 브라우저(Firefox)가 다운로드를 취소한다 — 한 박자 뒤에 푼다.
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      // ⚠️ `extractApiError`를 쓰지 않는다 — `responseType: 'blob'`이면 **에러 본문도 Blob**이라
      // 서버 문구를 읽을 수 없다(읽으려면 `blob.text()` + JSON 파싱이 한 겹 더 필요하다).
      showToast('서류를 내려받지 못했습니다. 잠시 후 다시 시도해 주세요.', 'error');
    } finally {
      setIsDownloadingDocId(null);
    }
  };

  // 지정서 삭제
  const handleDeleteVerificationDocument = async (docId: string) => {
    if (!companyId) return;
    setIsDeletingDocId(docId);
    try {
      await organizationsApi.deleteVerificationDocument(companyId, docId);
      setVerificationDocuments((prev) => prev.filter((d) => d.id !== docId));
      showToast('지정서가 삭제되었습니다.', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err) ?? '지정서를 삭제하지 못했습니다.', 'error');
    } finally {
      setIsDeletingDocId(null);
    }
  };

  // 카테고리 변경 성공 콜백
  const handleCategoryChangeSuccess = async () => {
    await loadOrganization();
    await fetchUser();
  };


  // 멤버 목록 로드
  const loadMembers = useCallback(async () => {
    if (!companyId) return;

    try {
      const memberList = await organizationsApi.getMembers(companyId);
      setMembers(memberList);
    } catch (err) {
      showToast(apiErrorMessage(err) ?? '멤버 목록을 불러오지 못했습니다.', 'error');
    }
  }, [companyId, showToast]);

  // 탈퇴 시나리오 계산 — 서버 `leaveOrganization`과 같은 축(company-rbac P4 ①): 소유자는 목록 항목의 `isOwner`(배정 기반),
  // 이전 후보는 「소유자가 아닌 소속 멤버」(P7-b-1: `isOrganizationMember` = 서버가 실은 `countsAsMember`). 서버가 최종 판정하지만
  // 화면이 role로 세면 답이 갈릴 수 있어 같은 필드를 읽는다(서버 쪽 `role !== null`은 병행 구간 뒤 P7-b-2에서 같은 축으로).
  const getLeaveScenario = useCallback((): LeaveScenario => {
    const myId = currentMembership?.userId;
    const owners = members.filter((m) => m.isOwner === true);
    const otherOwners = owners.filter((m) => m.userId !== myId);
    const transferCandidates = members.filter((m) => m.isOwner !== true && isOrganizationMember(m));

    if (otherOwners.length > 0) return 'OTHER_OWNERS';
    if (transferCandidates.length > 0) return 'TRANSFER_REQUIRED';
    return 'LAST_MEMBER';
  }, [members, currentMembership?.userId]);

  // 소유권 이전 대상 목록 — 소유자가 아닌 소속 멤버(외부 인원은 `countsAsMember=false`)
  const getSafetyManagers = useCallback(() => {
    return members.filter((m) => m.isOwner !== true && isOrganizationMember(m));
  }, [members]);

  // 탈퇴 모달 열기
  const handleOpenLeaveModal = async () => {
    await loadMembers();
    setShowLeaveModal(true);
    setLeaveMode(null);
    setSelectedNewOwner('');
    setConfirmOrgName('');
    setLeaveStep('choice');
  };

  // 탈퇴 모달 닫기
  const handleCloseLeaveModal = () => {
    setShowLeaveModal(false);
    setLeaveMode(null);
    setSelectedNewOwner('');
    setConfirmOrgName('');
    setLeaveStep('choice');
  };

  // 다음 단계로 이동
  const handleNextStep = () => {
    const scenario = getLeaveScenario();

    if (scenario === 'OTHER_OWNERS') {
      // 다른 Owner가 있으면 바로 탈퇴
      handleLeave();
    } else if (scenario === 'TRANSFER_REQUIRED') {
      if (leaveMode === 'transfer') {
        if (!selectedNewOwner) {
          showToast('소유권을 이전할 멤버를 선택해주세요.', 'error');
          return;
        }
        handleLeave();
      } else if (leaveMode === 'delete') {
        setLeaveStep('confirm');
      }
    } else {
      // LAST_MEMBER - 조직 삭제 확인 단계
      setLeaveStep('confirm');
    }
  };

  // 탈퇴 처리
  const handleLeave = async () => {
    if (!companyId) return;

    const scenario = getLeaveScenario();

    // 삭제가 포함된 경우 조직 이름 확인
    if (
      (scenario === 'LAST_MEMBER' || (scenario === 'TRANSFER_REQUIRED' && leaveMode === 'delete')) &&
      confirmOrgName !== organization?.name
    ) {
      showToast('조직 이름이 일치하지 않습니다.', 'error');
      return;
    }

    setIsLeaving(true);

    try {
      const options: LeaveOrganizationOptions = {};

      if (scenario === 'TRANSFER_REQUIRED') {
        if (leaveMode === 'transfer') {
          // MemberSelect는 member.id를 반환하므로 userId로 변환
          const selectedMember = safetyManagers.find((m) => m.id === selectedNewOwner);
          if (selectedMember) {
            options.transferToUserId = selectedMember.userId;
          }
        } else if (leaveMode === 'delete') {
          options.forceDelete = true;
        }
      } else if (scenario === 'LAST_MEMBER') {
        options.forceDelete = true;
      }

      const result: LeaveOrganizationResult = await organizationsApi.leave(companyId, options);

      if (result.requiresChoice) {
        // 선택이 필요한 경우 (서버에서 반환)
        showToast(result.message || '탈퇴 옵션을 선택해주세요.', 'info');
        return;
      }

      // 탈퇴 성공: 서버에서 ActionResultHelper를 통해 토스트/알림 전송
      // WebSocketProvider의 handleActionResult에서 처리됨 (토스트, 알림, 네비게이션 포함)
      handleCloseLeaveModal();
      // 네비게이션은 WebSocketProvider의 handleActionResult (org:left)에서 처리
    } catch (err) {
      showToast(apiErrorMessage(err) ?? '조직을 나가지 못했습니다.', 'error');
    } finally {
      setIsLeaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId || !organization) return;
    // 버튼 disabled를 우회하는 경로(입력에서 Enter)까지 무변경 저장을 막는다.
    if (!orgFormDirty.isDirty) return;

    // WORKSPACE(개인/팀)는 이름 편집 가능, 검증 대상 조직은 상호 고정
    const isNameEditable = organization.category === 'WORKSPACE';

    setIsSaving(true);

    try {
      const data: UpdateOrganizationDto = {
        timezone,
        allowNonAssignedSiteView, // 전 조직 공통 포함
      };

      // 이름 편집 가능한 경우에만 name 포함
      if (isNameEditable) {
        data.name = name.trim() || undefined;
      }

      const updated = await organizationsApi.update(companyId, data);
      setOrganization(updated);
      // 저장된 값이 새 기준점 — 재캡처하지 않으면 저장 직후에도 계속 "변경사항 있음"으로 남는다.
      captureOrgBaseline();
      // 성공 토스트는 서버에서 ActionResultHelper를 통해 전송됨

      // 사용자 정보 갱신 (조직명 변경 반영)
      await fetchUser();
    } catch (err) {
      if (!handleCompanyDuplicateError(err)) {
        showToast(apiErrorMessage(err) ?? '조직 정보를 저장하지 못했습니다.', 'error');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // 본사 정보 저장
  const handleSaveCompany = async () => {
    if (!companyId) return;
    if (!companyName.trim()) {
      showToast('회사명을 입력해주세요.', 'error');
      return;
    }

    setIsSavingCompany(true);
    try {
      const updated = await organizationsApi.update(companyId, {
        company: {
          name: companyName.trim(),
          registrationNumber: companyRegistrationNumber.trim() || undefined,
          ceoName: companyCeoName.trim() || undefined,
          openingDate: companyOpeningDate.trim() || undefined,
          industry: companyIndustry.trim() || undefined,
          contact: companyContact.trim() || undefined,
          email: companyEmail.trim() || undefined,
          address: companyAddress.trim() || undefined,
          zonecode: companyZonecode.trim() || undefined,
          addressDetail: companyAddressDetail.trim() || undefined,
        },
      });
      setOrganization(updated);
      showToast('사업자 정보가 저장되었습니다.', 'success');
    } catch (err) {
      if (!handleCompanyDuplicateError(err)) {
        showToast(apiErrorMessage(err) ?? '사업자 정보를 저장하지 못했습니다.', 'error');
      }
    } finally {
      setIsSavingCompany(false);
    }
  };

  // AI 작성 문체 저장 — 사업자 정보와 같은 결(카드 자체 저장 버튼, 같은 PATCH /organizations/:id).
  const handleSaveTone = async () => {
    if (!companyId) return;
    // 버튼 disabled를 우회하는 경로까지 무변경 저장을 막는다(운영 설정 폼과 같은 규칙).
    if (!toneDirty.isDirty) return;

    setIsSavingTone(true);
    try {
      const updated = await organizationsApi.update(companyId, { aiMeasureTone });
      setOrganization(updated);
      // 저장된 값이 새 기준점 — 재캡처하지 않으면 저장 직후에도 계속 "변경사항 있음"으로 남는다.
      captureToneBaseline();
      showToast('AI 작성 문체가 저장되었습니다.', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err) ?? 'AI 작성 문체를 저장하지 못했습니다.', 'error');
    } finally {
      setIsSavingTone(false);
    }
  };

  const isCompanyDirty = useMemo(() => {
    const current = {
      name: companyName.trim(),
      registrationNumber: companyRegistrationNumber.trim(),
      ceoName: companyCeoName.trim(),
      openingDate: companyOpeningDate.trim(),
      industry: companyIndustry.trim(),
      contact: companyContact.trim(),
      email: companyEmail.trim(),
      address: companyAddress.trim(),
      zonecode: companyZonecode.trim(),
      addressDetail: companyAddressDetail.trim(),
    };
    const initial = {
      name: (organization?.company?.name ?? '').trim(),
      registrationNumber: (organization?.company?.registrationNumber ?? '').trim(),
      ceoName: (organization?.company?.ceoName ?? '').trim(),
      openingDate: (organization?.company?.openingDate ?? '').trim(),
      industry: (organization?.company?.industry ?? '').trim(),
      contact: (organization?.company?.contact ?? '').trim(),
      email: (organization?.company?.email ?? '').trim(),
      address: (organization?.company?.address ?? '').trim(),
      zonecode: (organization?.company?.zonecode ?? '').trim(),
      addressDetail: (organization?.company?.addressDetail ?? '').trim(),
    };
    return JSON.stringify(current) !== JSON.stringify(initial);
  }, [
    companyName,
    companyRegistrationNumber,
    companyCeoName,
    companyOpeningDate,
    companyIndustry,
    companyContact,
    companyEmail,
    companyAddress,
    companyZonecode,
    companyAddressDetail,
    organization?.company,
  ]);

  // ⚠️ **현재 이 구독으로는 이 화면의 저장이 다른 세션에 실시간으로 반영되지 않는다.**
  // 이 화면의 두 저장(운영 설정·사업자 정보)은 둘 다 `PATCH /organizations/:id`인데
  // `organizations.service.update`에는 `emitEntityChanged`가 **없다**(끝에서 행위자 본인에게
  // 결과 토스트만 보낸다). `entity:'company'`를 쏘는 곳은 `companies.service.ts`의 협력업체
  // create·update **둘뿐**이고, 그 목록은 이 화면에 없다 — 즉 지금 이 구독이 실제로 하는 일은
  // 무관한 협력업체 쓰기마다 조직을 한 번 더 읽는 것이다(무해하지만 의도한 동기화는 아니다).
  // 서버 `update`에 emit이 추가되면 이 구독이 원래 의도(다른 Owner의 사업자 정보 변경을 조용히
  // 반영)대로 작동한다 — 배선은 그대로 두고 주석만 사실에 맞춘다(2026-09-19).
  // 세 폼(운영 설정·사업자 정보·AI 작성 문체) 중 하나라도 편집 중이면 미루고, 저장·되돌리기로 dirty가 풀릴 때 1회 재조회.
  useEntityChanged(['company'], () => loadOrganization({ silent: true }), {
    paused: orgFormDirty.isDirty || isCompanyDirty || toneDirty.isDirty,
  });

  // 다운로드 모달 열기
  const handleOpenDownloadModal = () => {
    setShowDownloadModal(true);
    setDownloadMethod('direct');
    setExportId(null);
    setExportStatus(null);
    setDownloadStep('method');
  };

  // 다운로드 모달 닫기
  const handleCloseDownloadModal = () => {
    setShowDownloadModal(false);
    setDownloadMethod('direct');
    setExportId(null);
    setExportStatus(null);
    setDownloadStep('method');
  };

  // 데이터 내보내기 요청
  const handleExportRequest = async () => {
    if (!companyId) return;

    // 수신처는 계정에 등록된 것만 쓴다 (S27 수신자 축) — 서버가 body의 phone/email을
    // 400으로 거부하므로, 여기서는 "보낼 곳이 있는가"만 본다.
    if (downloadMethod === 'sms' && !user?.phone) {
      showToast('등록된 휴대폰 번호가 없습니다. 프로필에서 먼저 등록해주세요.', 'error');
      return;
    }
    if (downloadMethod === 'email' && !user?.email) {
      showToast('등록된 이메일이 없습니다. 프로필에서 먼저 등록해주세요.', 'error');
      return;
    }

    setIsExporting(true);
    setDownloadStep('processing');

    try {
      const result = await dataExportApi.createOrganizationExport(companyId, downloadMethod);

      setExportId(result.exportId);
      setExportStatus(result.status);

      if (downloadMethod === 'direct') {
        // 직접 다운로드: 상태 폴링 시작
        pollExportStatus(result.exportId);
      } else {
        // 이메일/SMS: 안내 메시지 표시
        showToast(result.message || '다운로드 링크가 전송되었습니다.', 'success');
        handleCloseDownloadModal();
      }
    } catch (err) {
      showToast(apiErrorMessage(err) ?? '데이터 내보내기를 시작하지 못했습니다.', 'error');
      setDownloadStep('method');
    } finally {
      setIsExporting(false);
    }
  };

  // 내보내기 상태 폴링
  const pollExportStatus = useCallback(async (id: string) => {
    const maxAttempts = 60; // 최대 5분 (5초 간격)
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        showToast('데이터 생성 시간이 초과되었습니다. 나중에 다시 시도해주세요.', 'error');
        setDownloadStep('method');
        return;
      }

      try {
        const status = await dataExportApi.getExportStatus(id);
        setExportStatus(status.status);

        if (status.status === 'READY') {
          setDownloadStep('ready');
          showToast('데이터가 준비되었습니다.', 'success');
        } else if (status.status === 'FAILED') {
          showToast('데이터 생성에 실패했습니다.', 'error');
          setDownloadStep('method');
        } else if (status.status === 'GENERATING') {
          attempts++;
          setTimeout(poll, 5000); // 5초 후 재시도
        }
      } catch {
        attempts++;
        setTimeout(poll, 5000);
      }
    };

    poll();
  }, [showToast]);

  // 파일 다운로드
  const handleDownloadFile = async () => {
    if (!exportId) return;

    try {
      const blob = await dataExportApi.downloadFile(exportId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `safetyhub_export_${exportId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast('다운로드가 시작됩니다.', 'success');
      handleCloseDownloadModal();
    } catch (err) {
      // `responseType: 'blob'`이면 에러 본문도 Blob이라 서버 문구가 읽히지 않는다 — 대개 일반 문구로 떨어진다
      showToast(apiErrorMessage(err) ?? '파일을 내려받지 못했습니다.', 'error');
    }
  };

  // 시나리오 설명 텍스트
  const getScenarioDescription = () => {
    const scenario = getLeaveScenario();
    switch (scenario) {
      case 'OTHER_OWNERS':
        return '조직에서 탈퇴합니다. 다른 Owner가 조직을 계속 관리합니다.';
      case 'TRANSFER_REQUIRED':
        return '조직의 유일한 Owner입니다. 소유권 이전 또는 조직 삭제를 선택할 수 있습니다.';
      case 'LAST_MEMBER':
        return '조직의 마지막 멤버입니다. 탈퇴 시 조직이 30일 후 삭제됩니다.';
      default:
        return '';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  const scenario = getLeaveScenario();
  const safetyManagers = getSafetyManagers();

  // 회사 정본 축의 4단 사다리 — 저장은 2축이고 표시만 shared 헬퍼로 합친다
  const companyTrust = companyTrustLevel({
    verificationStatus: companyNtsStatus,
    claimedAt: companyClaimedAt,
  });

  return (
    <div className={isMobile ? 'space-y-4' : 'space-y-6'}>
      {/* 헤더 */}
      <div>
        <div className="flex items-center gap-3">
          <h1
            className={
              isMobile
                ? 'text-lg font-bold tracking-tight text-ink-900'
                : 'text-[26px] font-extrabold tracking-tight text-ink-900'
            }
          >
            조직 관리
          </h1>
          {verificationStatus === 'VERIFIED' && organization?.category !== 'WORKSPACE' && (
            <Badge tone="emerald">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              조직 인증 완료
              {organization?.verifiedAt && (
                <span className="ml-0.5 opacity-80">
                  ({new Date(organization!.verifiedAt).toLocaleDateString('ko-KR')})
                </span>
              )}
            </Badge>
          )}
          {/* 회사 정본 축은 조직 자격 심사와 별개 사다리다 — NTS 4필드 검증까지 끝났어도
              대표자 인증(클레임) 전이면 "완료"로 읽히지 않게 단계를 분리해 표시한다. */}
          {organization?.category !== 'WORKSPACE' &&
            (companyTrust === 'CANONICAL' ? (
              <Badge tone="emerald">대표자 인증 완료</Badge>
            ) : companyTrust === 'INFO_VERIFIED' ? (
              <Badge tone="slate">대표자 인증 전</Badge>
            ) : null)}
        </div>
        <p className={`mt-1 ${isMobile ? 'text-sm text-ink-500' : 'text-[15px] font-medium text-ink-500'}`}>
          {organization?.category === 'WORKSPACE'
            ? '작업 공간 기본 정보를 관리합니다.'
            : '조직 기본 정보를 관리합니다.'}
        </p>
      </div>

      {/* 인증 상태 배너 — 렌더 여부는 `verificationBand(...) !== 'none'`이 정한다.
          ⚠️ `!== 'NOT_REQUIRED' && !== 'VERIFIED'` 손열거로 되돌리지 말 것: 그 형태는 shared에
             멤버가 늘 때 아무것도 red로 만들지 않는 드리프트 입구였고, 지금은 그 판정이
             `lib/organizations`의 밴드 하나로 모여 spec의 Record가 미기재를 tsc로 거부한다. */}
      {organization && verificationBand(verificationStatus) !== 'none' && (() => {
        const _isWorkplace = organization.category === 'WORKPLACE';
        // 수동 심사 대역 — **집합의 정본은 `lib/organizations`의 `MANUAL_REVIEW_VERIFICATION_STATUSES`**다.
        // ⚠️ 여기 손열거로 되돌리지 말 것: 종전에 그래서 `REVIEW_AMBIGUOUS`가 빠졌고, 그 상태의
        //    조직에게 **수동 심사 대기가 「반려 — 사유 미제공」**(아래 rose 분기)으로 보였다.
        //    형제 `VerificationPendingBanner`는 그것을 `REVIEW_NO_MATCH`와 같게 처분하고 있어
        //    두 화면이 갈려 있었다(2026-10-20 — 그 상수 JSDoc이 경위의 정본).
        const _isManualReview = isManualReviewVerificationStatus(verificationStatus);
        const _isInfoMismatch = verificationStatus === 'INFO_MISMATCH';
        // 심사·정정 대역도 amber — 목업 세만틱 5색(orange 없음, yellow·orange→amber 수렴).
        // PENDING과 색이 같아지지만 구분은 아이콘(경고 삼각형 vs 시계)과 제목이 맡는다.
        //
        // ⚠️ **색·아이콘·제목 세 사슬의 폴백은 `'unknown'`이고 `'rejected'`가 아니다** — 사유는
        //    `verificationBand`의 JSDoc(요지: 모르는 상태를 「반려 — 사유 미제공」으로 보이던 것이
        //    2026-10-20 결함이고, `REJECTED`는 명시할 수 있으므로 폴백을 옮겨도 잃는 것이 없다).
        //    `_band === 'rejected'`를 지우고 `: rose`로 되돌리지 말 것.
        // ⚠️ 밴드는 **container·아이콘**만 정한다 — 제목·본문은 아래 `_isInfoMismatch`/
        //    `_isManualReview`가 계속 가른다(같은 amber 안에서 문구가 다르다).
        const _band = verificationBand(verificationStatus);
        return (
        <div className={`rounded-card p-4 ${
          _band === 'attention' ? 'bg-amber-50 border border-amber-200' :
          _band === 'progress' ? 'bg-amber-50 border border-amber-200' :
          _band === 'rejected' ? 'bg-rose-50 border border-rose-200' :
          'bg-ink-50 border border-ink-200'
        }`}>
          <div className="flex items-start gap-3">
            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
              _band === 'attention' ? 'bg-amber-100' :
              _band === 'progress' ? 'bg-amber-100' :
              _band === 'rejected' ? 'bg-rose-100' :
              'bg-ink-100'
            }`}>
              {_band === 'attention' ? (
                <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              ) : _band === 'rejected' ? (
                <svg className="w-5 h-5 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                /* progress · unknown — 둘 다 「기다리는 중」이라 시계를 쓰고 색만 갈린다 */
                <svg className={`w-5 h-5 ${_band === 'progress' ? 'text-amber-600' : 'text-ink-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h3 className={`text-sm font-semibold ${
                _band === 'attention' ? 'text-amber-800' :
                _band === 'progress' ? 'text-amber-800' :
                _band === 'rejected' ? 'text-rose-800' :
                'text-ink-800'
              }`}>
                {_isInfoMismatch
                   ? '사업자 정보 정정 필요'
                   : _isManualReview
                   /*
                     ⚠️ `verificationDocuments.length > 0`(**아무** 서류)로 되돌리지 말 것 —
                     그것이 2026-10-21에 고친 거짓 제목이다. 기관 조직이 `REVIEW_TIMEOUT`에서
                     **사업자등록증만** 올리면(`handleBatchUpload`가 두 칸을 독립적으로 보내므로
                     도달한다) 이 제목이 「지정서 제출 완료」라 말했고, 형제 배너는 같은 상태를
                     옳게 「서류 제출 필요」라 말해 **두 화면이 갈려 있었다**. 판정의 정본은
                     `lib/organizations`의 `hasRequiredVerificationDocs`이고 배너도 그것을 읽는다.
                   */
                   ? (hasRequiredVerificationDocs(verificationDocuments, organization.category)
                       ? (_isWorkplace
                           ? '사업자 인증 심사 진행 중'
                           : '기관 인증 심사 진행 중 — 지정서 제출 완료')
                       : (_isWorkplace
                           ? '사업자 인증 심사 대기 중'
                           : '기관 인증 심사 대기 중 — 지정서를 업로드해주세요'))
                   : _band === 'progress'
                   ? '국세청 사업자 정보 확인 중...'
                   : _band === 'rejected'
                   ? (organization.rejectionReasonCode
                       ? `반려 — ${REJECTION_REASON_LABELS[organization.rejectionReasonCode]}`
                       : `반려 — ${organization.rejectionReason || '사유 미제공'}`)
                   : '인증 상태를 확인하고 있습니다'}
              </h3>

              {verificationStatus === 'REJECTED' && organization.rejectionReasonCode && organization.rejectionReason && (
                <p className="mt-1 text-sm text-rose-700">
                  추가 메모: {organization.rejectionReason}
                </p>
              )}

              {/*
                주소를 지목하지 말 것 — 국세청 진위확인에 주소를 보내지 않으므로 확인 대상이 아니다.
                고칠 곳은 사업자등록번호·개업일자·대표자명·상호 네 가지뿐이다 (피드백 #51)
              */}
              {_isInfoMismatch && (
                <p className="mt-1 text-sm text-amber-700">
                  현재 입력한 사업자 정보가 국세청 기록과 일치하지 않습니다. 사업자등록번호·개업일자·대표자명·상호를
                  사업자등록증에 적힌 그대로 입력했는지 확인해 주세요. 주소는 확인 대상이 아닙니다.
                </p>
              )}

              {_band === 'progress' && (
                <p className="mt-1 text-xs text-amber-700">
                  국세청 API를 통해 사업자 정보를 자동 검증하고 있습니다.
                </p>
              )}

              {/*
                `'unknown'` — web이 모르는 상태값이다. **「몇 분 이내에 완료됩니다」류 약속을 쓰지
                말 것**: 형제 배너의 `'pending-retrying'` 문구가 그 약속을 담고 있어서 폴백을 그쪽에
                그대로 맞추면 **거짓 약속**이 된다(2026-10-20 판단). 여기서 할 수 있는 정직한 말은
                「모른다 + 아래 서류 UI는 그대로 쓸 수 있다」뿐이고, **맞는 문구를 주는 것은 그 멤버를
                더하는 사람의 몫**이다(그 사람은 spec Record의 red를 신호로 받는다).
              */}
              {_band === 'unknown' && (
                <p className="mt-1 text-xs text-ink-600">
                  인증 진행 상태를 가져오는 중입니다. 잠시 후 다시 확인해 주세요. 서류 제출은 아래에서
                  그대로 진행할 수 있습니다.
                </p>
              )}

              {/*
                업로드 UI — 실제로 도달하는 상태는 PENDING / REJECTED / INFO_MISMATCH / REVIEW_* 넷,
                그리고 **web이 모르는 상태**(밴드 `'unknown'`)다. 뒤의 것은 **의도적으로 포함**한다 —
                무슨 상태인지 몰라도 서류 제출은 막을 이유가 없고, 막으면 서버가 새 상태를 쓴 날
                사용자가 **아무것도 못 하게** 된다.
                ⚠️ **종전에 이 문장이 거짓이었다**(2026-10-21에 맞췄다): 아래 창구가 상태를 손열거해
                `REVIEW_AMBIGUOUS`·`REVIEW_REAPPLIED`와 모르는 상태가 `return null`로 떨어져
                **컨트롤이 0개**였다. 지금은 `verificationUploadSurface`가 union 밖에 가장 넓은
                창구를 주므로 참이다 — 그 함수를 `===` 사슬로 되돌리면 이 문장이 다시 거짓이 된다.
                ⚠️ 종전 이 주석이 `VERIFIED`도 열거하고 있었는데 그것은 **도달 불가**다: 이 블록을 여는
                상위 게이트가 `NOT_REQUIRED`·`VERIFIED`를 **배제**하므로(지금은 밴드 `'none'`) 인증이 끝나면 서류 섹션이
                통째로 사라진다(그래서 `renderDocList` 호출부가 전부 이 블록 안이고, 두 종류가
                **같은 헬퍼**를 쓴다).
                ⚠️ 종전 이 자리가 그 호출부 수를 **손으로 적고 있었고 그 수가 틀렸다**(2026-10-27에
                걷었다). 세려면 **`grep -n 'renderDocList'`로 세라** — 정의도 함께 잡힌다.
                그 손으로 적은 수가 줄기 `code-review-1011-fixes.md`의 배당문으로 옮겨져 **라벨 자리
                분모를 과대**로 만들었다 ⇒ **수를 손으로 적지 않는다**(세는 쪽이 정본이고, 틀린 수를
                **옳은 수로 바꾸는 것도 처방이 아니다** — 그러면 다음에 또 낡는다).
                ⚠️ 그것을 「서류가 안전하다」로 읽지 말 것 — 이 게이트는 **화면에만** 있고 서버는
                조건 없이 서류를 응답에 싣는다(`organizations.service.ts`의 `verificationDocuments`
                조립). 2026-10-19 측정에서 dev·prod 모두 서류 보유 조직이 전부 `VERIFIED`라 이 UI를
                보는 조직이 0개였는데도 **노출은 실재했다**는 것이 그 가름이다.
              */}
              {(() => {
                // ⚠️ 분류 규칙을 여기 다시 적지 말 것 — 종전엔 이 두 `filter`가 형제
                //    `VerificationPendingBanner`의 사본이었고(그쪽 주석이 「동일한 기준」이라
                //    자진 신고했다) 정본은 `lib/organizations`의
                //    `classifyVerificationDocuments`다. `?? 'DESIGNATION_CERT'` 기본값의 사유
                //    (지정서가 먼저 생겨 옛 행에 `type`이 없다)도 그 함수 JSDoc에 있다.
                const { businessRegistration: businessRegDocs, designation: designationDocs } =
                  classifyVerificationDocuments(verificationDocuments);

                // 서류 목록 렌더링 헬퍼
                const renderDocList = (docs: VerificationDocument[]) => (
                  <div className="mt-2 space-y-1.5">
                    {docs.map((doc) => (
                      <div key={doc.id} className="flex items-center gap-2 text-xs">
                        <svg className="w-4 h-4 text-ink-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {/*
                          ⚠️ `<a href={doc.url}>`로 되돌리지 말 것 — 인증 전용 라우트라 평범한
                          내비게이션이 401이다(사유는 `handleDownloadVerificationDocument` 주석).

                          ⚠️ `TextButton`(packages/ui)으로 바꾸지도 말 것. 그 프리미티브는
                          **「밑줄이 붙은 것은 이 갈래가 아니다 — 링크 시맨틱이므로 `<a>`로 둔다」**
                          고 선언하는데, 이 자리는 그 전제(밑줄 ⟺ 내비게이션)가 성립하지 않는
                          예외다: **보이는 것은 링크**(파일을 여는 자리)이고 **하는 일은 인증
                          요청**이라 `<a>`도 `TextButton`도 맞지 않는다. 그래서 날 `button`에
                          링크 시각을 둔다 — 클래스는 종전 `<a>`의 것을 그대로 옮겨 **시각이 바뀌지
                          않게** 했고(`cursor-pointer`만 더했다 — tailwind v3 preflight는 버튼에
                          그것을 넣지 않아 그 한 줄이 없으면 커서가 화살표로 바뀐다), 바꿀 일이
                          생기면 두 갈래 중 하나로 옮기는 대신 이 예외를 다시 판정할 것.
                        */}
                        <button
                          type="button"
                          onClick={() => handleDownloadVerificationDocument(doc)}
                          disabled={isDownloadingDocId === doc.id}
                          aria-busy={isDownloadingDocId === doc.id}
                          className="text-brand-600 hover:underline truncate flex-1 text-left cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {doc.fileName}
                        </button>
                        <span className="text-ink-400 flex-shrink-0">
                          {new Date(doc.uploadedAt).toLocaleDateString('ko-KR')}
                        </span>
                        {/* 서버 `DELETE :organizationId/verification-documents/:docId` = org.settings.manage */}
                        {canManageOrgSettings && (
                          <TextButton
                            tone="danger"
                            type="button"
                            onClick={() => handleDeleteVerificationDocument(doc.id)}
                            disabled={isDeletingDocId === doc.id}
                            aria-label="서류 삭제"
                            className="flex-shrink-0"
                          >
                            {isDeletingDocId === doc.id ? (
                              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                            ) : (
                              <XMarkIcon className="w-4 h-4" />
                            )}
                          </TextButton>
                        )}
                      </div>
                    ))}
                  </div>
                );

                // 공통 input accept
                const fileAccept = '.pdf,.jpg,.jpeg,.png,.heic,.heif,image/heic,image/heif';

                /*
                  **서류 제출 창구는 `lib/organizations`의 `verificationUploadSurface` 하나가 정한다.**

                  ⚠️ 상태별 `===` 손열거로 되돌리지 말 것 — 그것이 2026-10-21에 고친 결함이다.
                  종전엔 이 자리가 `PENDING` / `REVIEW_TIMEOUT` / `REVIEW_NO_MATCH` /
                  `REJECTED || INFO_MISMATCH` **네 자리**를 손으로 열거하고 나머지를 아래
                  `return null`로 떨어뜨렸다. 그래서 수동 심사 대역 넷 중
                  `REVIEW_AMBIGUOUS`·`REVIEW_REAPPLIED`가 **업로드 컨트롤 0개**였는데, 같은 화면의
                  제목이 「지정서를 업로드해주세요」라 말하고 형제 배너의 「서류 업로드하기」 버튼이
                  **그리로 보냈다** — 사용자가 작업을 끝낼 수 없는 막다른 길이다. 밴드 `'unknown'`도
                  같은 자리로 떨어져 바로 위 두 주석(「모르는 상태는 **의도적으로 포함**한다」·
                  「서류 제출은 아래에서 **그대로 진행할 수 있습니다**」)이 **거짓**이었다.
                  경위·근거(서버가 받는 집합 · 카테고리별 도달 가능성)는 그 함수 JSDoc이 정본.
                */
                const _surface = verificationUploadSurface(verificationStatus);

                // 자동 재검증 중(`PENDING`) — **서버가 업로드를 거부**하므로 컨트롤을 그리지
                // 않는다(그리면 눌렀을 때 실패하는 화면이 된다 — `ORGANIZATION_VERIFICATION_NOT_ESCALATED`).
                if (_surface === 'auto-retrying') {
                  return (
                    <p className="mt-2 text-xs text-amber-700">
                      자동 재검증 중입니다. 지정 시간이 지나면 운영자 심사로 전환됩니다.
                    </p>
                  );
                }

                // 수동 심사 대역 넷 + **web이 모르는 상태**: 배치 업로드
                // (서버 `POST :organizationId/verification-documents` = org.settings.manage)
                if (_surface === 'required-only' || _surface === 'required-and-business') {
                  // 권한이 없으면 올릴 수 없으므로 컨트롤을 그리지 않는다(종전 동작 유지).
                  // ⚠️ 그래도 위 제목은 「업로드해주세요」라 말한다 — 그 문구의 권한 축은 이
                  //    라운드의 범위가 아니다(별 항목으로 보고했다).
                  if (!canManageOrgSettings) return null;

                  /*
                    어느 칸을 띄우나 — **상태가 아니라 「카테고리 필수 서류 + NTS 해소 여부」**에서
                    파생한다. 그래서 종전의 `&& !_isWorkplace` 가드가 필요 없어진다:
                      기관  + required-only          → 지정서만            (= 종전 REVIEW_NO_MATCH)
                      기관  + required-and-business  → 지정서 + 사업자등록증 (= 종전 REVIEW_TIMEOUT)
                      WORKPLACE                     → 사업자등록증 한 칸   (필수 서류가 그것이라 접힌다)
                    ⚠️ 그 가드를 「결함」으로 읽지 말 것 — `REVIEW_NO_MATCH`·`REVIEW_AMBIGUOUS`는
                       WORKPLACE에 **도달하지 않는다**(서버가 NTS 통과 즉시 `VERIFIED`로
                       early-return한다). 바뀐 것은 동작이 아니라 **같은 답을 구조로 얻는 것**이고,
                       실제로 넓어진 자리는 `REVIEW_REAPPLIED`(WORKPLACE에도 도달한다)와
                       모르는 상태다.
                  */
                  const _requiredType = requiredVerificationDocType(organization.category);
                  const _showDesignation = _requiredType === 'DESIGNATION_CERT';
                  const _showBusinessReg =
                    _surface === 'required-and-business' ||
                    _requiredType === 'BUSINESS_REGISTRATION';

                  return (
                    <div className="mt-3 space-y-4">
                      {_showBusinessReg && (
                        <input
                          type="file"
                          ref={businessRegFileInputRef}
                          className="hidden"
                          accept={fileAccept}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              if (file.size > 10 * 1024 * 1024) {
                                showToast('파일 크기가 10MB를 초과합니다.', 'error');
                                e.target.value = '';
                                return;
                              }
                              setPendingBusinessReg(file);
                            }
                            e.target.value = '';
                          }}
                        />
                      )}
                      {_showDesignation && (
                        <input
                          type="file"
                          ref={designationFileInputRef}
                          className="hidden"
                          accept={fileAccept}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              if (file.size > 10 * 1024 * 1024) {
                                showToast('파일 크기가 10MB를 초과합니다.', 'error');
                                e.target.value = '';
                                return;
                              }
                              setPendingDesignation(file);
                            }
                            e.target.value = '';
                          }}
                        />
                      )}

                      {/* 사업자등록증 섹션 — NTS 미해소(`required-and-business`)이거나 그것이
                          카테고리 필수 서류(WORKPLACE)일 때 */}
                      {_showBusinessReg && (
                      <Card padding="sm" elevation="none" className="space-y-1">
                        <p className="text-xs font-semibold text-ink-700">
                          {VERIFICATION_DOCUMENT_TYPE_LABELS.BUSINESS_REGISTRATION}
                        </p>
                        {businessRegDocs.length > 0 && renderDocList(businessRegDocs)}
                        {pendingBusinessReg && (
                          <div className="flex items-center gap-2 text-xs bg-brand-50 border border-brand-200 rounded-card-sm px-2 py-1.5 mt-2">
                            <svg className="w-4 h-4 text-brand-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span className="text-brand-700 truncate flex-1">{pendingBusinessReg.name}</span>
                            <span className="text-brand-500 text-xs flex-shrink-0">
                              {(pendingBusinessReg.size / 1024).toFixed(0)}KB
                            </span>
                            <TextButton
                              tone="danger"
                              type="button"
                              onClick={() => setPendingBusinessReg(null)}
                              className="flex-shrink-0"
                              aria-label="선택 취소"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </TextButton>
                          </div>
                        )}
                        {!pendingBusinessReg && businessRegDocs.length === 0 && (
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => businessRegFileInputRef.current?.click()}
                            className="mt-1.5 text-amber-700 hover:text-amber-800"
                          >
                            <DownloadIcon className="w-3.5 h-3.5 rotate-180" />
                            {VERIFICATION_DOCUMENT_TYPE_LABELS.BUSINESS_REGISTRATION} 파일 선택
                          </Button>
                        )}
                        {pendingBusinessReg && (
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => businessRegFileInputRef.current?.click()}
                            className="mt-1.5"
                          >
                            다른 파일 선택
                          </Button>
                        )}
                      </Card>
                      )}

                      {/* 지정서 섹션 — 기관 전용(카테고리 필수 서류가 지정서일 때) */}
                      {_showDesignation && (
                        <Card padding="sm" elevation="none" className="space-y-1">
                          <p className="text-xs font-semibold text-ink-700">
                            {VERIFICATION_DOCUMENT_TYPE_LABELS.DESIGNATION_CERT}
                          </p>
                          {designationDocs.length > 0 && renderDocList(designationDocs)}
                          {pendingDesignation && (
                            <div className="flex items-center gap-2 text-xs bg-brand-50 border border-brand-200 rounded-card-sm px-2 py-1.5 mt-2">
                              <svg className="w-4 h-4 text-brand-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className="text-brand-700 truncate flex-1">{pendingDesignation.name}</span>
                              <span className="text-brand-500 text-xs flex-shrink-0">
                                {(pendingDesignation.size / 1024).toFixed(0)}KB
                              </span>
                              <TextButton
                                tone="danger"
                                type="button"
                                onClick={() => setPendingDesignation(null)}
                                className="flex-shrink-0"
                                aria-label="선택 취소"
                              >
                                <XMarkIcon className="w-4 h-4" />
                              </TextButton>
                            </div>
                          )}
                          {!pendingDesignation && designationDocs.length === 0 && (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => designationFileInputRef.current?.click()}
                              className="mt-1.5 text-amber-700 hover:text-amber-800"
                            >
                              <DownloadIcon className="w-3.5 h-3.5 rotate-180" />
                              {VERIFICATION_DOCUMENT_TYPE_LABELS.DESIGNATION_CERT} 파일 선택
                            </Button>
                          )}
                          {pendingDesignation && (
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => designationFileInputRef.current?.click()}
                              className="mt-1.5"
                            >
                              다른 파일 선택
                            </Button>
                          )}
                        </Card>
                      )}

                      {/* 배치 업로드 버튼 — ⚠️ 칸이 숨은 쪽의 pending 상태는 세지 않는다.
                          상태가 세션 중에 바뀌면(업로드 → 서버가 새 상태를 돌려준다) 보이지 않는
                          파일 때문에 버튼만 뜨는 자리가 생긴다. */}
                      {((_showBusinessReg && pendingBusinessReg) ||
                        (_showDesignation && pendingDesignation)) && (
                        <Button
                          type="button"
                          variant="warning"
                          onClick={handleBatchUpload}
                          disabled={isBatchUploading}
                          className="w-full"
                        >
                          {isBatchUploading ? '업로드 중...' : '선택한 서류 업로드'}
                        </Button>
                      )}
                    </div>
                  );
                }

                // 반려·정보 불일치 — 사업자 정보를 고쳐 재검증받는 경로. 컨트롤 모양이 위
                // 배치 업로드와 달라(즉시 업로드 + 안내 문구) 창구 값을 따로 둔다.
                if (_surface === 'reapply') {
                  return (
                    <div className="mt-3 space-y-4">
                      {canManageOrgSettings && (
                        <>
                          {_isWorkplace ? (
                            /* WORKPLACE: NTS 자동 검증으로 충분 — 서류 업로드 불필요 */
                            <p className="text-xs text-brand-700 bg-brand-50 border border-brand-200 rounded-card p-3">
                              사업자 정보를 수정하면 자동으로 재검증됩니다.
                            </p>
                          ) : (
                            /* 기관 (SAFETY_MANAGEMENT_AGENCY, GUIDANCE_AGENCY): 지정서만 */
                            <>
                              <input
                                type="file"
                                ref={designationFileInputRef}
                                className="hidden"
                                accept={fileAccept}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleUploadVerificationDocument(file, 'DESIGNATION_CERT');
                                  e.target.value = '';
                                }}
                              />

                              {/* 지정서 섹션 */}
                              <Card padding="sm" elevation="none" className="space-y-1">
                                <p className="text-xs font-semibold text-ink-700">
                                  {VERIFICATION_DOCUMENT_TYPE_LABELS.DESIGNATION_CERT}
                                </p>
                                {verificationStatus !== 'REJECTED' && designationDocs.length > 0 && renderDocList(designationDocs)}
                                <Button
                                  type="button"
                                  variant={verificationStatus === 'REJECTED' ? 'danger' : 'secondary'}
                                  size="sm"
                                  onClick={() => designationFileInputRef.current?.click()}
                                  disabled={isUploadingDesignation}
                                  className={
                                    verificationStatus === 'REJECTED'
                                      ? 'mt-1.5'
                                      : 'mt-1.5 text-amber-700 hover:text-amber-800'
                                  }
                                >
                                  {isUploadingDesignation ? (
                                    <>
                                      <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                      </svg>
                                      업로드 중...
                                    </>
                                  ) : (
                                    <>
                                      <DownloadIcon className="w-3.5 h-3.5 rotate-180" />
                                      {VERIFICATION_DOCUMENT_TYPE_LABELS.DESIGNATION_CERT} 업로드
                                    </>
                                  )}
                                </Button>
                              </Card>
                            </>
                          )}
                        </>
                      )}

                      {/* 안내 문구 */}
                      {!_isWorkplace && (
                        <p className="text-xs text-ink-500">
                          사업자 정보 수정은 아래 '사업자 정보' 섹션에서 진행해주세요.
                        </p>
                      )}
                    </div>
                  );
                }

                // `'none'`(인증이 필요 없거나 끝났다) — 이 블록을 여는 상위 게이트가 밴드
                // `'none'`을 배제하므로 **도달 불가**다. ⚠️ 그래서 이 `return null`을 「나머지
                // 상태의 폴백」으로 다시 쓰지 말 것 — 그렇게 쓰던 것이 위 막다른 길의 기제다.
                return null;
              })()}

            </div>
          </div>
        </div>
        );
      })()}

      {/* 넓은 화면에서는 설정 본문(운영 설정·사업자 정보)을 왼쪽 2/3에 두고,
          로고·데이터 관리·위험 구역처럼 이따금 쓰는 것들을 오른쪽 rail로 모은다. */}
      <div className={`${isMobile ? 'space-y-4' : 'space-y-6'} lg:grid lg:grid-cols-3 lg:gap-6 lg:space-y-0`}>
        {/* ── 좌: 설정 본문 ── */}
        <div className={`lg:col-span-2 ${isMobile ? 'space-y-4' : 'space-y-6'}`}>

      {/* 운영 설정 폼 */}
      <form onSubmit={handleSubmit} className="rounded-card-lg border border-line bg-white shadow-card p-6 space-y-6">
        {/* 조직 유형 - WORKSPACE 제외 (검증 대상 조직만) */}
        {organization?.category !== 'WORKSPACE' && (
          <div>
            <label className={FORM_LABEL_CLASS}>
              조직 유형
            </label>
            <div className="mt-1 flex items-center gap-3">
              {/* 값 표시 전용(변경은 옆 버튼→모달)이라 input이 아니지만, 같은 줄의 폼 컨트롤과
                  높이·테두리가 어긋나면 안 돼서 FORM_CONTROL_CLASS의 disabled 결을 리터럴로 맞춘다. */}
              <div className="flex h-[42px] flex-1 items-center rounded-[10px] border border-line bg-ink-50 px-3 text-[14px] font-semibold text-ink-500">
                {CATEGORY_OPTIONS.find((opt) => opt.value === category)?.label ?? category}
              </div>
              {/* 조직 유형 변경도 `PATCH :organizationId`(update) = org.settings.manage */}
              {canManageOrgSettings && (
                <Button
                  type="button"
                  onClick={() => setShowCategoryModal(true)}
                  className="whitespace-nowrap"
                >
                  변경
                </Button>
              )}
            </div>
          </div>
        )}

        {/* 조직 표시 이름 - WORKSPACE만 (이름 편집 가능) */}
        {organization?.category === 'WORKSPACE' && (
          <div>
            <label htmlFor="name" className={FORM_LABEL_CLASS}>
              조직 표시 이름 <span className="text-ink-400">(선택)</span>
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={FORM_CONTROL_CLASS}
              placeholder="비워두면 회사명을 사용합니다"
            />
            <p className="mt-1 text-xs text-ink-500">
              기본적으로 회사명으로 표시됩니다. 별칭을 사용하려면 입력하세요.
            </p>
          </div>
        )}

        {/* 시간대 */}
        <div>
          <label htmlFor="timezone" className={FORM_LABEL_CLASS}>
            시간대
          </label>
          <select
            id="timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className={FORM_CONTROL_CLASS}
          >
            <option value="Asia/Seoul">Asia/Seoul (KST, +09:00)</option>
            <option value="Asia/Tokyo">Asia/Tokyo (JST, +09:00)</option>
            <option value="Asia/Shanghai">Asia/Shanghai (CST, +08:00)</option>
            <option value="UTC">UTC (협정 세계시)</option>
          </select>
          <p className="mt-1 text-xs text-ink-500">
            조직의 기본 시간대입니다. 현장별로 다른 시간대를 설정할 수 있습니다.
          </p>
        </div>

        {/* 비담당자 점검 접근 설정 - 전 조직 */}
        <div className="border-t pt-6">
          <div className="flex items-center justify-between">
            <div>
              {/* 토글 행의 라벨이라 폼 컨트롤 라벨(FORM_LABEL_CLASS)과 일부러 다르다 —
                  아래 설명문과 한 덩어리로 읽혀야 해서 굵기·크기를 본문 결로 둔다. */}
              <label htmlFor="allowNonAssignedSiteView" className="block text-sm font-medium text-ink-700">
                비담당 안전관리자 점검 조회 허용
              </label>
              <p className="mt-1 text-xs text-ink-500">
                활성화하면 현장에 배정되지 않은 안전관리자도 모든 점검을 읽기 전용으로 조회할 수 있습니다.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={allowNonAssignedSiteView}
              onClick={() => setAllowNonAssignedSiteView(!allowNonAssignedSiteView)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${
                allowNonAssignedSiteView ? 'bg-brand-600' : 'bg-ink-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  allowNonAssignedSiteView ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>

        {/* 버튼 */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <DirtyHint isDirty={orgFormDirty.isDirty} className="mr-auto text-sm" />
          <Button type="submit" variant="primary" disabled={isSaving || !orgFormDirty.isDirty}>
            {isSaving ? '저장 중...' : '저장'}
          </Button>
        </div>
      </form>

      {/* WORKSPACE 조직 안내 */}
      {organization?.category === 'WORKSPACE' && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-card p-4">
          <p className="text-sm text-emerald-800 font-medium">개인/팀</p>
          <p className="text-sm text-emerald-700 mt-1">
            사업자 등록 없이 사용하는 개인 또는 팀 작업공간입니다. 사업자 인증 및 본사 정보 입력 없이 현장, 점검, 보고서 등 모든 기능을 사용할 수 있습니다.
          </p>
        </div>
      )}

      {/* 사업자 정보 - WORKSPACE 제외 (검증 대상 조직만) */}
      {organization?.category !== 'WORKSPACE' && (
        <Card padding="xl" radius="lg" elevation="md">
          <h3 className={`${CARD_SECTION_TITLE_CLASS} mb-1`}>사업자 정보</h3>
          <p className="text-sm text-ink-500 mb-4">보고서에 표시되는 사업자 정보를 관리합니다.</p>
          {/* 넓어진 카드에서 한 줄에 하나씩 두면 입력칸만 길어진다 — 짧은 필드는 두 칸씩,
              주소와 저장 버튼만 전폭. gap-4는 원래 space-y-4와 같은 세로 리듬이다. */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-x-4">
            <div>
              <label htmlFor="company-name" className={FORM_LABEL_CLASS}>
                회사명 <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                id="company-name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={isSavingCompany}
                className={FORM_CONTROL_CLASS}
                placeholder="예: ABC 건설 주식회사"
              />
            </div>

            <div>
              <label htmlFor="company-reg-number" className={FORM_LABEL_CLASS}>
                사업자등록번호
              </label>
              <input
                type="text"
                id="company-reg-number"
                value={companyRegistrationNumber}
                onChange={(e) => setCompanyRegistrationNumber(e.target.value)}
                disabled={isSavingCompany}
                className={FORM_CONTROL_CLASS}
                placeholder="예: 123-45-67890"
              />
            </div>

            <div>
              <label htmlFor="company-ceo-name" className={FORM_LABEL_CLASS}>
                대표자명
              </label>
              <input
                type="text"
                id="company-ceo-name"
                value={companyCeoName}
                onChange={(e) => setCompanyCeoName(e.target.value)}
                disabled={isSavingCompany}
                className={FORM_CONTROL_CLASS}
                placeholder="예: 홍길동"
              />
            </div>

            <div>
              <label htmlFor="company-opening-date" className={FORM_LABEL_CLASS}>
                개업일
              </label>
              <input
                type="text"
                id="company-opening-date"
                value={companyOpeningDate}
                onChange={(e) => setCompanyOpeningDate(e.target.value)}
                disabled={isSavingCompany}
                className={FORM_CONTROL_CLASS}
                placeholder="예: 20200101 (YYYYMMDD)"
              />
            </div>

            <div>
              <label htmlFor="company-industry" className={FORM_LABEL_CLASS}>
                업종
              </label>
              <input
                type="text"
                id="company-industry"
                value={companyIndustry}
                onChange={(e) => setCompanyIndustry(e.target.value)}
                disabled={isSavingCompany}
                className={FORM_CONTROL_CLASS}
                placeholder="예: 제조업, 건설업"
              />
            </div>

            <div>
              <label htmlFor="company-contact" className={FORM_LABEL_CLASS}>
                대표 연락처
              </label>
              <input
                type="text"
                id="company-contact"
                value={companyContact}
                onChange={(e) => setCompanyContact(e.target.value)}
                disabled={isSavingCompany}
                className={FORM_CONTROL_CLASS}
                placeholder="예: 02-1234-5678"
              />
            </div>

            <div>
              <label htmlFor="company-email" className={FORM_LABEL_CLASS}>
                이메일
              </label>
              <input
                type="email"
                id="company-email"
                value={companyEmail}
                onChange={(e) => setCompanyEmail(e.target.value)}
                disabled={isSavingCompany}
                className={FORM_CONTROL_CLASS}
                placeholder="예: contact@company.com"
              />
            </div>

            <div className="sm:col-span-2">
              <AddressSearch
                id="company-address"
                appearance="aligned"
                address={companyAddress}
                addressDetail={companyAddressDetail}
                zonecode={companyZonecode}
                onChange={(data) => {
                  setCompanyAddress(data.address);
                  setCompanyAddressDetail(data.addressDetail);
                  setCompanyZonecode(data.zonecode);
                }}
                label="주소"
                disabled={isSavingCompany}
              />
            </div>

            {/* 게이트는 이미 있었고(isCompanyDirty) 이유 표시만 붙인다 — 같은 페이지의 운영 설정
                폼이 이유를 말하는데 여기만 말없이 비활성이면 같은 규칙으로 읽히지 않는다. */}
            <div className="flex items-center justify-end gap-3 pt-2 border-t sm:col-span-2">
              <DirtyHint isDirty={isCompanyDirty} className="mr-auto text-sm" />
              <Button
                type="button"
                variant="primary"
                onClick={handleSaveCompany}
                disabled={isSavingCompany || !isCompanyDirty}
              >
                {isSavingCompany ? '저장 중...' : '사업자 정보 저장'}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* 회사 인증(정본 클레임) - WORKSPACE 제외. 사업자 정보 4필드 검증이 전제라 그 카드 바로 아래에 둔다. */}
      {organization?.category !== 'WORKSPACE' && isOwner && companyId && (
        <CompanyClaimSection
          companyId={companyId}
          ntsVerificationStatus={companyNtsStatus}
          claimedAt={companyClaimedAt}
          defaultCeoName={companyCeoName}
          onClaimed={loadOrganization}
        />
      )}

      {/* 조직 통합 요청 — 정본(클레임 완료)일 때만 자격이 생기므로 회사 인증 카드 바로 아래.
          합칠 조직도 이력도 없으면 스스로 사라진다. */}
      {organization?.category !== 'WORKSPACE' && isOwner && companyId && (
        <TenantMergeSection
          companyId={companyId}
          registrationNumber={companyRegistrationNumber}
          claimedAt={companyClaimedAt}
        />
      )}

      {/* AI 작성 문체 — 전 조직(WORKSPACE 포함). 저장은 사업자 정보와 같은 결(카드 자체 버튼).
          결정 기록 2026-09-20-ai-measure-tone-preset §7. */}
      {(() => {
        const defaultTone = resolveAiMeasureTone(null, category);
        const effectiveTone = resolveAiMeasureTone(aiMeasureTone, category);
        const categoryLabel =
          CATEGORY_OPTIONS.find((opt) => opt.value === category)?.label ?? category;
        const defaultLabel =
          AI_MEASURE_TONE_PRESETS.find((p) => p.value === defaultTone)?.label ?? defaultTone;
        return (
          <Card padding="xl" radius="lg" elevation="md">
            <h3 className={`${CARD_SECTION_TITLE_CLASS} mb-1`}>AI 작성 문체</h3>
            <p className="text-sm text-ink-500">
              AI가 정리하는 위험요인·개선대책 문장의 문체를 정합니다.
            </p>
            <p className="text-sm text-ink-500 mb-4">
              기본값은 조직 유형에서 정해집니다 — {categoryLabel}: {defaultLabel}
            </p>

            {/* 라디오 카드 — ChangeCategoryModal의 label-wrap 라디오 패턴. 선택은 저장값이 아니라
                파생 유효값(effectiveTone)으로 그린다: 저장값이 null이면 조직 유형 기본값 카드가 선택된다. */}
            <fieldset className="space-y-2" disabled={isSavingTone}>
              <legend className="sr-only">AI 작성 문체</legend>
              {AI_MEASURE_TONE_PRESETS.map((preset) => {
                const checked = effectiveTone === preset.value;
                return (
                  <label
                    key={preset.value}
                    className={`flex items-start gap-3 p-3 rounded-card border transition-colors ${
                      canManageOrgSettings ? 'cursor-pointer' : 'cursor-default'
                    } ${
                      checked
                        ? 'border-brand-500 bg-brand-50'
                        : 'border-ink-200 bg-white hover:border-ink-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="aiMeasureTone"
                      value={preset.value}
                      checked={checked}
                      onChange={() => setAiMeasureTone(toStoredAiMeasureTone(preset.value, category))}
                      disabled={!canManageOrgSettings}
                      className="mt-0.5 h-4 w-4 text-brand-600 border-ink-300 focus:ring-brand-500"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink-800">
                        <span>
                          {preset.label}
                          <span className="ml-1 font-normal text-ink-500">— {preset.description}</span>
                        </span>
                        {preset.value === defaultTone && (
                          <Badge tone="slate" size="sm">조직 유형 기본값</Badge>
                        )}
                      </span>
                      <span className="mt-1 block text-xs text-ink-500">“{preset.example}”</span>
                    </span>
                  </label>
                );
              })}
            </fieldset>

            <div className="mt-3 rounded-card bg-ink-50 p-3 text-xs text-ink-500">
              <p>변경은 이후 AI가 새로 생성·정제하는 문장부터 적용됩니다. 이미 작성된 보고서와 결재 완료본은 바뀌지 않습니다.</p>
              <p className="mt-1">위험요인 문장과 양식의 고정 문구는 영향받지 않습니다.</p>
            </div>

            {/* AI 문체 저장은 `PATCH :organizationId`(update) = org.settings.manage */}
            {canManageOrgSettings && (
              <div className="mt-4 flex items-center justify-end gap-3 border-t pt-4">
                <DirtyHint isDirty={toneDirty.isDirty} className="mr-auto text-sm" />
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleSaveTone}
                  disabled={isSavingTone || !toneDirty.isDirty}
                >
                  {isSavingTone ? '저장 중...' : '문체 저장'}
                </Button>
              </div>
            )}
          </Card>
        );
      })()}

        </div>

        {/* ── 우: 로고·데이터·위험 구역 ── */}
        <div className={isMobile ? 'space-y-4' : 'space-y-6'}>

      {/* 조직 로고 - WORKSPACE 제외 (검증 대상 조직만) */}
      {organization?.category !== 'WORKSPACE' && (
        <LogoUploadSection
          companyId={companyId!}
          logoPath={organization?.logoPath}
          onUpdated={loadOrganization}
        />
      )}

      {/* 데이터 관리 */}
      <Card padding="xl" radius="lg" elevation="md">
        <h3 className={`${CARD_SECTION_TITLE_CLASS} mb-4`}>데이터 관리</h3>
        <p className="text-sm text-ink-500 mb-4">
          조직의 모든 데이터(점검, 보고서, TBM 등)를 내보낼 수 있습니다.
        </p>
        <Button type="button" onClick={handleOpenDownloadModal}>
          <DownloadIcon className="h-5 w-5" />
          조직 데이터 내보내기
        </Button>
      </Card>

      {/* 위험 구역 - 전 조직 노출 (H-9a: 탈퇴/삭제 제한 제거) */}
      <Card padding="xl" radius="lg" elevation="md">
        <h3 className="text-[15px] font-extrabold tracking-tight text-rose-800 mb-4">위험 구역</h3>
        <p className="text-sm text-ink-500 mb-4">
          {getScenarioDescription()}
        </p>
        <button
          type="button"
          className="px-4 py-2 text-sm font-medium text-rose-600 bg-white border border-rose-300 rounded-control hover:bg-rose-50"
          onClick={handleOpenLeaveModal}
        >
          조직 탈퇴
        </button>
      </Card>

        </div>
      </div>

      {/* 탈퇴 모달 — 시나리오별 아이콘+제목이 본문 머리라 base 헤더엔 제목을 두지 않고 ✕만 둔다.
          탈퇴 처리 중에는 닫는 길을 전부 막는다(종전엔 오버레이·✕로 도중에 닫혔다).
          본문 안의 「데이터 다운로드」가 아래 다운로드 모달을 **이 모달 위에** 연다 — 그쪽 층은 여는 쪽이 정한다. */}
      <Modal
        open={showLeaveModal}
        onClose={handleCloseLeaveModal}
        ariaLabel="조직 탈퇴"
        size="lg"
        closeOnOverlay={!isLeaving}
        closeOnEsc={!isLeaving}
        showCloseButton={!isLeaving}
        closeLocked={isLeaving}
        footer={
          <>
            {scenario === 'TRANSFER_REQUIRED' && leaveStep === 'confirm' && (
              <Button
                type="button"
                onClick={() => setLeaveStep('choice')}
              >
                이전
              </Button>
            )}

            <Button type="button" onClick={handleCloseLeaveModal} disabled={isLeaving}>
              취소
            </Button>

            {scenario === 'OTHER_OWNERS' && (
              <Button
                type="button"
                variant="danger"
                onClick={handleLeave}
                disabled={isLeaving}
              >
                {isLeaving ? '처리 중...' : '탈퇴'}
              </Button>
            )}

            {scenario === 'TRANSFER_REQUIRED' && leaveStep === 'choice' && (
              <Button
                type="button"
                variant="danger"
                onClick={handleNextStep}
                disabled={!leaveMode || (leaveMode === 'transfer' && !selectedNewOwner)}
              >
                다음
              </Button>
            )}

            {((scenario === 'LAST_MEMBER') ||
              (scenario === 'TRANSFER_REQUIRED' && leaveMode === 'delete' && leaveStep === 'confirm')) && (
              <Button
                type="button"
                variant="danger"
                onClick={handleLeave}
                disabled={confirmOrgName !== organization?.name || isLeaving}
              >
                {isLeaving ? '처리 중...' : '조직 탈퇴'}
              </Button>
            )}
          </>
        }
      >
                {/* OTHER_OWNERS: 단순 탈퇴 */}
                {scenario === 'OTHER_OWNERS' && (
                  <>
                    <div className="text-center mb-4">
                      <ExclamationTriangleIcon className="h-12 w-12 text-amber-500 mx-auto" />
                      <h3 className="text-lg font-semibold mt-4">조직에서 탈퇴하시겠습니까?</h3>
                    </div>
                    <p className="text-sm text-ink-600">
                      탈퇴 후에는 이 조직의 데이터에 접근할 수 없습니다.
                      다시 참여하려면 Owner의 초대가 필요합니다.
                    </p>
                    <p className="mt-4 text-sm text-ink-500">
                      탈퇴 전 데이터를 백업하시려면{' '}
                      <TextButton
                        className="hover:underline"
                        type="button"
                        onClick={handleOpenDownloadModal}
                      >
                        데이터 다운로드
                      </TextButton>
                      를 이용하세요.
                    </p>
                  </>
                )}

                {/* TRANSFER_REQUIRED: 선택 필요 */}
                {scenario === 'TRANSFER_REQUIRED' && leaveStep === 'choice' && (
                  <>
                    <div className="text-center mb-4">
                      <ExclamationTriangleIcon className="h-12 w-12 text-amber-500 mx-auto" />
                      <h3 className="text-lg font-semibold mt-4">마지막 Owner입니다</h3>
                    </div>
                    <p className="text-sm text-ink-600 mb-4">
                      탈퇴하려면 아래 중 하나를 선택하세요.
                    </p>

                    <div className="space-y-3">
                      <button
                        type="button"
                        onClick={() => setLeaveMode('transfer')}
                        className={`w-full p-4 border rounded-card text-left transition-colors ${
                          leaveMode === 'transfer'
                            ? 'border-brand-500 bg-brand-50'
                            : 'border-line hover:border-ink-300'
                        }`}
                      >
                        <strong className="block">소유권 이전 후 탈퇴</strong>
                        <p className="text-sm text-ink-500 mt-1">
                          다른 멤버에게 Owner 권한을 넘기고 탈퇴합니다.
                        </p>
                      </button>

                      {leaveMode === 'transfer' && (
                        <div className="pl-4 border-l-2 border-brand-500">
                          <label className={FORM_LABEL_CLASS}>
                            소유권을 이전할 멤버 선택
                          </label>
                          <MemberSelect
                            members={safetyManagers}
                            value={selectedNewOwner}
                            onChange={setSelectedNewOwner}
                            placeholder="선택해주세요"
                          />
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => setLeaveMode('delete')}
                        className={`w-full p-4 border rounded-card text-left transition-colors ${
                          leaveMode === 'delete'
                            ? 'border-rose-500 bg-rose-50'
                            : 'border-line hover:border-ink-300'
                        }`}
                      >
                        <strong className="block text-rose-600">조직 삭제 후 탈퇴</strong>
                        <p className="text-sm text-ink-500 mt-1">
                          조직과 모든 데이터가 30일 후 삭제됩니다.
                        </p>
                      </button>
                    </div>
                  </>
                )}

                {/* LAST_MEMBER 또는 TRANSFER_REQUIRED + delete: 삭제 확인 */}
                {((scenario === 'LAST_MEMBER') ||
                  (scenario === 'TRANSFER_REQUIRED' && leaveMode === 'delete' && leaveStep === 'confirm')) && (
                  <>
                    <div className="text-center mb-4">
                      <ExclamationTriangleIcon className="h-12 w-12 text-rose-500 mx-auto" />
                      <h3 className="text-lg font-semibold mt-4">조직이 삭제됩니다</h3>
                    </div>

                    <div className="text-sm text-ink-600">
                      <p>탈퇴 시 조직이 30일 후 삭제되며:</p>
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>모든 현장, 점검, 보고서, TBM이 삭제됩니다</li>
                        <li>다른 멤버들도 조직에서 제외됩니다</li>
                        <li>이 조직에 다시 접근할 수 없습니다</li>
                      </ul>
                    </div>

                    {/* 데이터 다운로드 안내 */}
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-card">
                      <p className="text-sm text-amber-700">
                        삭제 전 조직 데이터를 백업하시겠습니까?
                      </p>
                      <TextButton
                        type="button"
                        className="mt-2 text-sm hover:underline"
                        onClick={handleOpenDownloadModal}
                      >
                        데이터 다운로드 요청
                      </TextButton>
                    </div>

                    {/* 조직 이름 확인 */}
                    <div className="mt-4">
                      <label className="text-sm text-ink-600">
                        확인을 위해 조직 이름을 입력하세요: <strong>{organization?.name}</strong>
                      </label>
                      {/* 파괴적 확인 입력이라 포커스 강조만 rose — 치수·굵기는 FORM_CONTROL_CLASS와 같은 값. */}
                      <input
                        type="text"
                        value={confirmOrgName}
                        onChange={(e) => setConfirmOrgName(e.target.value)}
                        className="mt-1 block w-full h-[42px] rounded-[10px] border border-rose-200 bg-white px-3 text-[14px] font-semibold text-ink-900 placeholder:text-ink-400 placeholder:font-medium focus:outline-none focus:border-rose-500 focus:ring-[3px] focus:ring-rose-100"
                        placeholder="조직 이름 입력"
                      />
                    </div>
                  </>
                )}
      </Modal>

      {/* 카테고리 변경 모달 */}
      {organization && (
        <ChangeCategoryModal
          isOpen={showCategoryModal}
          onClose={() => setShowCategoryModal(false)}
          onSuccess={handleCategoryChangeSuccess}
          companyId={companyId!}
          currentCategory={category}
          hasCompany={!!organization.company}
        />
      )}

      {/* FULLY Company 중복 차단 모달 */}
      {companyDuplicateModal}


      {/* 데이터 다운로드 모달 — 진입이 둘이다: 페이지의 「데이터 다운로드」(단독)와 탈퇴 모달 본문 안의
          링크(탈퇴 모달 **위**). 층은 여는 쪽이 정한다는 규약(`modalLayers`)대로 탈퇴 모달이 열려 있으면 sub로
          올린다 — 종전엔 둘 다 같은 z에 DOM 순서로만 겹쳤다. 닫기 잠금은 두지 않는다(종전에도 생성 중에
          닫을 수 있었고, 닫으면 폴링 상태를 초기화한다). */}
      <Modal
        open={showDownloadModal}
        onClose={handleCloseDownloadModal}
        title="데이터 다운로드"
        size="md"
        overlayZClassName={showLeaveModal ? MODAL_LAYER.sub : MODAL_LAYER.root}
        overlayBgClassName={showLeaveModal ? SUB_MODAL_OVERLAY_BG : undefined}
        footer={
          downloadStep === 'method' ? (
            <>
              <Button type="button" onClick={handleCloseDownloadModal}>
                취소
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleExportRequest}
                disabled={
                  isExporting ||
                  (downloadMethod === 'sms' && !user?.phone) ||
                  (downloadMethod === 'email' && !user?.email)
                }
              >
                {isExporting ? '요청 중...' : '다운로드 요청'}
              </Button>
            </>
          ) : undefined
        }
      >
                {/* 방식 선택 단계 */}
                {downloadStep === 'method' && (
                  <>
                    <p className="text-sm text-ink-600 mb-4">
                      조직의 모든 데이터(점검, 보고서, TBM 등)를 ZIP 파일로 내보냅니다.
                    </p>

                    <div className="space-y-3">
                      {/* 직접 다운로드 */}
                      <button
                        type="button"
                        onClick={() => setDownloadMethod('direct')}
                        className={`w-full p-4 border rounded-card text-left transition-colors ${
                          downloadMethod === 'direct'
                            ? 'border-brand-500 bg-brand-50'
                            : 'border-line hover:border-ink-300'
                        }`}
                      >
                        <strong className="block">직접 다운로드</strong>
                        <p className="text-sm text-ink-500 mt-1">
                          준비되면 바로 다운로드합니다.
                        </p>
                      </button>

                      {/* 이메일 */}
                      <button
                        type="button"
                        onClick={() => setDownloadMethod('email')}
                        disabled={!user?.email}
                        className={`w-full p-4 border rounded-card text-left transition-colors ${
                          downloadMethod === 'email'
                            ? 'border-brand-500 bg-brand-50'
                            : 'border-line hover:border-ink-300'
                        }`}
                      >
                        <strong className="block">이메일로 받기</strong>
                        <p className="text-sm text-ink-500 mt-1">
                          {user?.email
                            ? `등록된 주소(${user.email})로 전송합니다. (7일간 유효)`
                            : '등록된 이메일이 없어 선택할 수 없습니다.'}
                        </p>
                      </button>

                      {/* SMS */}
                      <button
                        type="button"
                        onClick={() => setDownloadMethod('sms')}
                        disabled={!user?.phone}
                        className={`w-full p-4 border rounded-card text-left transition-colors ${
                          downloadMethod === 'sms'
                            ? 'border-brand-500 bg-brand-50'
                            : 'border-line hover:border-ink-300'
                        }`}
                      >
                        <strong className="block">SMS로 받기</strong>
                        <p className="text-sm text-ink-500 mt-1">
                          {user?.phone
                            ? `등록된 번호(${user.phone})로 전송합니다. (7일간 유효)`
                            : '등록된 휴대폰 번호가 없어 선택할 수 없습니다.'}
                        </p>
                      </button>
                    </div>

                  </>
                )}

                {/* 생성 중 단계 */}
                {downloadStep === 'processing' && (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
                    <p className="mt-4 text-ink-600">데이터를 준비하고 있습니다...</p>
                    <p className="mt-2 text-sm text-ink-500">
                      {exportStatus === 'GENERATING' ? '파일 생성 중...' : '데이터 양에 따라 몇 분이 소요될 수 있습니다.'}
                    </p>
                  </div>
                )}

                {/* 다운로드 준비 완료 단계 */}
                {downloadStep === 'ready' && (
                  <div className="text-center py-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                      <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="mt-4 text-ink-600">데이터가 준비되었습니다!</p>
                    <Button type="button" variant="primary" onClick={handleDownloadFile} className="mt-4">
                      다운로드
                    </Button>
                  </div>
                )}
      </Modal>
    </div>
  );
}

// ============================================
// 로고 업로드 섹션 컴포넌트
// ============================================

function LogoUploadSection({
  companyId,
  logoPath,
  onUpdated,
}: {
  companyId: string;
  logoPath?: string | null;
  onUpdated: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { showToast } = useToastStore();

  // 로고 URL: logoPath가 있으면 API 엔드포인트에서 조회 (캐시 버스팅 포함)
  const [cacheBuster, setCacheBuster] = useState(Date.now());
  const logoUrl = logoPath
    ? `${organizationsApi.getLogoUrl(companyId)}?t=${cacheBuster}`
    : null;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 클라이언트 측 검증
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      showToast('JPEG 또는 PNG 형식만 지원합니다.', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('파일 크기는 5MB 이하여야 합니다.', 'error');
      return;
    }

    setIsUploading(true);
    try {
      await organizationsApi.uploadLogo(companyId, file);
      setCacheBuster(Date.now());
      onUpdated();
      showToast('로고가 업로드되었습니다.', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err) ?? '로고를 업로드하지 못했습니다.', 'error');
    } finally {
      setIsUploading(false);
      // 파일 입력 초기화
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!confirm('로고를 삭제하시겠습니까?')) return;

    setIsDeleting(true);
    try {
      await organizationsApi.deleteLogo(companyId);
      onUpdated();
      showToast('로고가 삭제되었습니다.', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err) ?? '로고를 삭제하지 못했습니다.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Card padding="xl" radius="lg" elevation="md">
      <h3 className={`${CARD_SECTION_TITLE_CLASS} mb-4`}>조직 로고</h3>
      <div className="flex items-center gap-6">
        {/* 로고 미리보기 */}
        <div className="flex-shrink-0">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="조직 로고"
              className="w-20 h-20 rounded-card-sm object-contain border border-ink-200 bg-ink-50"
            />
          ) : (
            <div className="w-20 h-20 rounded-card-sm bg-ink-100 border border-ink-200 flex items-center justify-center text-ink-400 text-sm">
              로고 없음
            </div>
          )}
        </div>

        {/* 업로드/삭제 버튼 */}
        <div className="space-y-2">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? '업로드 중...' : logoPath ? '로고 변경' : '로고 업로드'}
            </Button>
            {logoPath && (
              // 파괴 액션 outline 결 — 조직 탈퇴 버튼과 같은 문자열(공용 Button에 outline
              // danger variant가 없어 수제 유지, ui-primitive-convergence에서 승격 검토)
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="h-8 px-3 text-xs font-semibold text-rose-600 bg-white border border-rose-300 rounded-control hover:bg-rose-50 disabled:opacity-50"
              >
                {isDeleting ? '삭제 중...' : '삭제'}
              </button>
            )}
          </div>
          <p className="text-xs text-ink-500">JPEG, PNG 형식 / 최대 5MB</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      </div>
    </Card>
  );
}
